import Grid, { GridSize } from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import React from "react";
import { ui } from "../../../interfaces/interfaces";
import Autocomplete from '@mui/material/Autocomplete';
import { isAsProperty, isDev } from "@utils/general";
import ContactModal from "../ContactModal";
import DeleteButtonBR from "../DeleteIconBR";
import ContactButtonBR from "../ContactIconBR";
import ContactButtonPF from "../ContactIconPF";
import { formHandler } from '../../../utilities/general';
import { handleBasicChange } from '../../../utilities/general';
import useDebugInformation from "../../../hooks/useDebugInformation";
import { Paper, Stack } from "@mui/material";
import MemoizedFormInput from "../MemoizedFormInput";
import { ContactFields, ContactPrefix } from "@utils/BasicTypes";
import { useSanitizer } from "@utils/validation/hooks";
import { StringProcessor } from "@utils/stringProcessor";
import { useAppSelector } from "@store/hooks";

const name = "ContactTab"
const form = formHandler(ui)

const titles = [
    "Mr",
    "Mrs",
    "Miss",
    "Ms",
    "Sir",
    "Madam",
    "Dr",
    "Rev",
    "Mx",
]

type A11yProps = {
    (name: string, prefix: ContactPrefix): {
        id: string
        name: string
        fullWidth: boolean
        autoComplete: string
        "aria-controls": string
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    }
}

const a11yProps: A11yProps = (name, prefix) => {
    if (prefix && name && !form.list.includes(`${prefix}${name}`))
        form.list.push(`${prefix}${name}`)
    if (!form.list.includes(`${prefix}Title`)) form.list.push(`${prefix}Title`)
    return {
        id: `input - ${name.toLowerCase()} `,
        name,
        fullWidth: true,
        autoComplete: 'none',
        onChange: (event) => { handleBasicChange(event, prefix) },
        "aria-controls": `action - input - ${name.toLowerCase()} `,
    };
}

type MemoizedFormInputProps = {
    parent: string
    prefix: Props["prefix"]
    inputName: ContactFields | "Title"
    xs: GridSize
    sm: GridSize
    disabled: boolean
}

const MemoizedPhoneInput = React.memo<MemoizedFormInputProps>(({ parent, prefix, inputName, xs, sm, disabled }) => {
    const monitored = useAppSelector(({ ui }) => ui[`${prefix}${inputName}`]);
    const valid = useAppSelector(({ state }) => state[`${prefix}${inputName}Valid`]);
    const altValid = useAppSelector(({ state }) => state[`${prefix}${inputName.toLowerCase().includes("landline") ? "Mobile" : "Landline"}Valid`]);
    // check if previously validated data in use
    const asProperty = isAsProperty(prefix, parent)
    const sanitized = useSanitizer(monitored || "", inputName, asProperty)
    const helperText = useAppSelector(({ ui }) => ui[`${prefix}${inputName}HelperText`])

    useDebugInformation(`${parent}▷[${inputName}]`, { parent, inputName, prefix, xs, sm, sanitized, valid, altValid })
    return (
        (<Grid
            size={{
                xs,
                sm
            }}> <TextField disabled={disabled} value={sanitized} required={!valid || !altValid} {...a11yProps(inputName, prefix)} label={StringProcessor.camelToWords(inputName)} error={!valid && !altValid} color={valid ? "primary" : "error"} helperText={!valid ? helperText : ""} /></Grid>)
    );
})

const MemoizedTitleInput = React.memo<MemoizedFormInputProps>(({ parent, prefix, inputName, xs, sm, disabled }) => {
    const monitored = useAppSelector(({ ui }) => ui[`${prefix}${inputName}`]);
    // check if previously validated data in use
    const asProperty = isAsProperty(prefix, parent)
    const sanitized = useSanitizer(monitored || "", inputName, asProperty);
    const helperText = useAppSelector(({ ui }) => ui[`${prefix}${inputName}HelperText`])
    const valid = useAppSelector(({ state }) => state[`${prefix}${inputName}Valid`]);

    useDebugInformation(`${parent}▷[${inputName}]`, { inputName, parent, prefix, xs, sm, sanitized, valid, helperText })
    return (
        (<Grid
            size={{
                xs,
                sm
            }}>
            <Autocomplete
                id="combo-box-titles"
                options={titles}
                isOptionEqualToValue={(option, value) => (option === value || value === "")}
                getOptionLabel={(option) => option || ""}
                onChange={React.useCallback((e: any, v: any) => ui[`${prefix}${inputName}`] = v as never, [inputName, prefix])}
                value={sanitized}
                renderInput={(params) => <TextField required {...params} label={StringProcessor.camelToWords(inputName)} name={StringProcessor.camelToWords(inputName)} disabled={disabled} error={!valid} helperText={!valid ? helperText : ""} />}
            />
        </Grid>)
    );
})

type Props = {
    prefix: ContactPrefix
    children?: any
    min?: boolean
    related?: boolean
    parent: string
}

const ContactTab: React.FC<Props> = ({ parent, prefix, children, min, related }) => {
    const a11Props = React.useMemo(() => ({ children }), [children])
    const modal = useAppSelector(({ ui }) => ui[`${prefix}ModalOpen`])
    const asContact = useAppSelector(({ ui }) => ui[`${prefix}AsContact`])
    const debugName = `${parent}▷[${name}]`

    const validatedPrefix = React.useMemo(() => asContact ? "PropertyContact" : prefix, [asContact, prefix])

    useDebugInformation(debugName, { prefix, parent, a11Props, modal, min, name, related })
    return (
        (<Stack spacing={1}>
            <Grid container spacing={1} >
                <MemoizedTitleInput inputName="Title" parent={debugName} prefix={validatedPrefix} xs={12} sm={4} disabled={asContact} />
                <MemoizedFormInput form={form} inputName="FirstName" parent={debugName} prefix={validatedPrefix} xs={12} sm={8} disabled={asContact} />
                <MemoizedFormInput form={form} inputName="LastName" parent={debugName} prefix={validatedPrefix} xs={12} sm={12} disabled={asContact} />
                {!min && <MemoizedPhoneInput inputName="Mobile" parent={debugName} prefix={validatedPrefix} xs={12} sm={6} disabled={asContact} />}
                {!min && <MemoizedPhoneInput inputName="Landline" parent={debugName} prefix={validatedPrefix} xs={12} sm={6} disabled={asContact} />}
                {(!min && !related) && <MemoizedFormInput form={form} inputName="Email" parent={debugName} prefix={validatedPrefix} xs={isDev() ? 9 : 10} sm={isDev() ? 9 : 10} disabled={asContact} />}
                {related && <MemoizedFormInput form={form} inputName="Relationship" parent={debugName} prefix={"RelatedContact"} xs={10} sm={10} disabled={asContact} />}
                <Grid
                    sx={{ whiteSpace: "nowrap" }}
                    size={{
                        xs: isDev() ? 3 : 2,
                        sm: isDev() ? 3 : 2
                    }}>
                    <Paper elevation={1} sx={{ p: .5, borderRadius: ".5rem", position: "relative" }}>
                        <ContactButtonBR parent={debugName} tooltip="Select Contact" prefix={prefix} disabledBtn={asContact} />
                        {isDev() && <ContactButtonPF parent={debugName} tooltip="Select Contact" prefix={prefix} disabledBtn={asContact} />}
                        <DeleteButtonBR parent={debugName} tooltip="Clear Form" action={React.useCallback(() => form.clear(prefix), [prefix])} disabledBtn={asContact} />
                    </Paper>
                </Grid>
                {React.useMemo(() => (< Stack > {a11Props.children}</Stack>), [a11Props.children])}
            </Grid>
            {modal && <ContactModal prefix={prefix} />}
        </Stack >)
    );
}

export default ContactTab;
